import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Collapse,
  Divider,
  FormControlLabel,  
  Checkbox, 
  Switch,
  ListSubheader,
  Chip,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  FileText,
  HelpCircle,
  X,
  Eye,
  GripVertical,
  Plus,
  Trash2,
  Edit2,
  Wand2,
  Settings,
  ChevronDown,
  ChevronRight,
  Image, 
  CheckCircle as CheckCircleIcon,
  User as UserIcon,
  CheckCircle,
  AlertCircle,
  Link,
  ArrowRight, 
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import PageHeader from './PageHeader';
import ImageControls from './ImageControls';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';
import WordPressClient from '../utils/WordPressClient';
import aiClient from '../api/clients/AiClient';
import imageClient from '../api/clients/ImageClient';
import { formatContent } from '../utils/contentFormatting';
import { useAIModels } from '../hooks/useAIModels';
import ModelSelector from './ModelSelector';

const ArticleResponseDialog = ({ 
  open, 
  onClose, 
  success, 
  error, 
  articleData,
  onPreview 
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        p: 3,
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        textAlign: 'center'
      }}>
        <Box sx={{ 
          width: 60, 
          height: 60, 
          borderRadius: '50%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 2,
          background: success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          color: success ? '#10B981' : '#EF4444'
      }}>
        {success ? (
            <CheckCircle size={30} strokeWidth={2} />
          ) : (
            <AlertCircle size={30} strokeWidth={2} />
          )}
        </Box>
        
        <Typography variant="h5" sx={{ 
          fontWeight: 600,
          mb: 1,
          color: '#1F2937'
        }}>
          {success ? 'Article Created' : 'Generation Failed'}
            </Typography>
           
        <Typography variant="body1" sx={{ 
          color: 'text.secondary',
          mb: 3,
          maxWidth: '90%'
        }}>
          {success 
            ? 'Your article has been successfully created and saved as a draft in WordPress.' 
            : error || 'An error occurred while generating your article.'}
        </Typography>
        
        {success && (
          <Box sx={{ 
            width: '100%',
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            mt: 1
          }}>
          <Button 
            onClick={onPreview}
            variant="outlined"
              startIcon={<Eye size={18} />}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '8px',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                color: '#1F2937',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#3B82F6',
                  bgcolor: 'rgba(59, 130, 246, 0.04)'
                }
              }}
          >
            Preview Article
          </Button>
            <Button
              onClick={onClose}
              variant="contained"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '8px',
                bgcolor: '#3B82F6',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#2563EB'
                }
              }}
            >
              Create Another Article
            </Button>
          </Box>
        )}
        
        {!success && (
        <Button 
          onClick={onClose}
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: '8px',
              bgcolor: '#3B82F6',
              fontWeight: 500,
              mt: 1,
              '&:hover': {
                bgcolor: '#2563EB'
              }
            }}
          >
            Try Again
        </Button>
        )}
      </Box>
    </Dialog>
  );
};

const StyledContent = styled(DialogContent)(({ theme }) => ({
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    margin: '1rem 0',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
    fontWeight: 600,
  },
  '& p': {
    marginBottom: '1rem',
    lineHeight: 1.7,
  },
}));

const ArticlePreviewDialog = ({ open, onClose, article }) => {
  if (!article) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="h6">Article Preview</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <StyledContent>
        <Typography variant="h4" gutterBottom>
          {article.title}
        </Typography>
        
        {article.featuredImage && (
          <Box sx={{ my: 2 }}>
            <img 
              src={article.featuredImage} 
              alt={article.title}
              style={{ 
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px'
              }} 
            />
          </Box>
        )}

        <Box 
          sx={{ mt: 3 }}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </StyledContent>

      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 2
      }}>
        <Button onClick={onClose}>
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// LoadingOverlay component for improved generation feedback
const LoadingOverlay = ({ visible, status, steps }) => {
  if (!visible) return null;
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 500,
          width: '100%',
          bgcolor: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          p: 4,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #E04C16 0%, #FF6B3D 100%)',
            mb: 3,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -5,
              left: -5,
              right: -5,
              bottom: -5,
              borderRadius: '50%',
              border: '2px solid rgba(224, 76, 22, 0.2)',
              animation: 'pulse 2s infinite',
            },
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              color: 'white',
            }}
          />
        </Box>
        
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1F2937' }}>
          Creating Your Article
        </Typography>
        
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
          {status || 'Please wait while we generate your article...'}
        </Typography>
        
        <Box sx={{ width: '100%', mb: 2 }}>
          {steps.map((step) => (
            <Box
              key={step.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: step.status === 'pending' ? 0.5 : 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  mr: 2,
                  fontSize: '0.8rem',
                  ...(step.status === 'completed'
                    ? {
                        bgcolor: '#10B981',
                        color: 'white',
                      }
                    : step.status === 'in-progress'
                    ? {
                        bgcolor: '#3B82F6',
                        color: 'white',
                      }
                    : step.status === 'error'
                    ? {
                        bgcolor: '#EF4444',
                        color: 'white',
                      }
                    : {
                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                        color: 'text.secondary',
                      }),
                }}
              >
                {step.status === 'completed' ? (
                  <CheckCircle size={16} />
                ) : step.status === 'error' ? (
                  <AlertCircle size={16} />
                ) : step.status === 'in-progress' ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : (
                  step.icon || <CheckCircle size={16} />
                )}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: step.status === 'error' ? '#EF4444' : 'text.primary',
                  }}
                >
                  {step.text}
                </Typography>
                {step.message && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {step.message}
                  </Typography>
                )}
              </Box>
              {step.status === 'completed' && <CheckCircle size={16} color="#10B981" />}
              {step.status === 'error' && <AlertCircle size={16} color="#EF4444" />}
            </Box>
          ))}
        </Box>
        
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontStyle: 'italic',
            mt: 2,
          }}
        >
          This may take a minute or two depending on the article length
        </Typography>
      </Box>
    </Box>
  );
};

const SingleArticle = ({onGeneratingChange}) => {

  const { authenticatedRequest, error: authError, loading: authLoading } = useAuth();

  const { models: availableModels, loading: modelsLoading, error: modelsError } = useAIModels(authenticatedRequest);
  const getAuxiliaryModel = () => {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return availableModels[0]?.value;
  };

const [isGenerating, setIsGenerating] = useState(false);
const [navigationTarget, setNavigationTarget] = useState(null);
const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
const [navDialogOpen, setNavDialogOpen] = useState(false);


  
  const [optimizeForSearch, setOptimizeForSearch] = useState(false);
  
  // SEO Keywords state
  const [seoKeywords, setSeoKeywords] = useState('');
  const [keywordMode, setKeywordMode] = useState('manual'); // 'manual' or 'generate'
  const [generatedKeywords, setGeneratedKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [generateMetaFields, setGenerateMetaFields] = useState(true); // Set to true by default to always generate
  const [isGeneratingMetaFields, setIsGeneratingMetaFields] = useState(false);
  const [autoGenerateSeo, setAutoGenerateSeo] = useState(true); // New state for auto-generation

  const [generationStatus, setGenerationStatus] = useState('');
  const [generationSteps, setGenerationSteps] = useState([
    { id: 'init', text: 'Preparing Content Framework', status: 'pending', icon: <Settings size={16} /> },
    { id: 'content', text: 'Crafting Article Content', status: 'pending', icon: <FileText size={16} /> },
    { id: 'images', text: 'Generating & Placing Images', status: 'pending', icon: <Image size={16} /> },
    { id: 'upload', text: 'Publishing to WordPress', status: 'pending', icon: <ArrowRight size={16} /> }
  ]);
  

  // Basic form state
  const [title, setTitle] = useState('');
  const [subheadings, setSubheadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [expandedSubheading, setExpandedSubheading] = useState(null);

  // WordPress settings
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);


  // AI settings
  const [model, setModel] = useState(''); 

  const [prompts, setPrompts] = useState({
    systemTemplates: [],
    userPrompts: []
  });
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [isGeneratingSubheadings, setIsGeneratingSubheadings] = useState(false);

  // Image settings
  const [includeImages, setIncludeImages] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(false);
  const [featuredImageModel, setFeaturedImageModel] = useState('fal-ai/flux/schnell');
  const [featuredImageStyle, setFeaturedImageStyle] = useState('realistic'); 
  const [subheadingImages, setSubheadingImages] = useState(false);
  const [subheadingImageModel, setSubheadingImageModel] = useState('fal-ai/flux/schnell');
  const [dimensions, setDimensions] = useState('square');
  const [subheadingImageStyle, setSubheadingImageStyle] = useState('realistic');
  const [maxSubheadings, setMaxSubheadings] = useState(5);
  const [imageFrequency, setImageFrequency] = useState('every');
  const [featuredImagePrompt, setFeaturedImagePrompt] = useState('');
  const [wpClient, setWpClient] = useState(null);


  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState(null);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState(null);

  useEffect(() => {
    if (!authLoading) {  
      fetchInitialData();
      initializeServices();
    }
  }, [authLoading]);

  useEffect(() => {
    const initializeAI = async () => {
      if (availableModels.length > 0) {
        try {
          const keys = await authenticatedRequest(() => apiClient.getApiKeys());
          
          // Initialize AI client
          aiClient.setApiKeys(keys);
          aiClient.updateModelConfigs(availableModels);
          
          // Initialize image client
          imageClient.setAvailableModels(availableModels);
          imageClient.setAiClient(aiClient);
          imageClient.setApiKeys(keys);
          
          // Set initial model if none selected
          if (!model && availableModels.length > 0) {
            const defaultModel = availableModels[0].value;
            console.log('Setting default model:', defaultModel);
            setModel(defaultModel);
          }
  
        } catch (error) {
          console.error('Error initializing AI client:', error);
          setError('Failed to initialize AI services');
        }
      }
    };
  
    initializeAI();
  }, [availableModels, authenticatedRequest]);

  useEffect(() => {
    if (selectedSite) {
      fetchCategories();
      fetchUsers();
      setSelectedUser(''); 
    }
  }, [selectedSite]);
  

  useEffect(() => {
    if (!authLoading && availableModels.length > 0) {
      console.log('Available AI Models:', {
        total: availableModels.length,
        models: availableModels.map(m => ({
          value: m.value,
          label: m.label,
          provider: m.provider
        }))
      });
    }
  }, [authLoading, availableModels]);

  


  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isGenerating) {
        e.preventDefault();
        setNavDialogOpen(true);
        e.returnValue = '';
        return '';
      }
    };
  
    const handleUnload = (e) => {
      if (isGenerating) {
        e.preventDefault();
        setNavDialogOpen(true);
        return false;
      }
    };
  
    const handlePopState = (event) => {
      if (isGenerating) {
        event.preventDefault();
        window.history.pushState(null, '', window.location.pathname);
        setNavDialogOpen(true);
      }
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('popstate', handlePopState);
    
    if (isGenerating) {
      window.history.pushState(null, '', window.location.pathname);
    }
  
    onGeneratingChange?.(isGenerating);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('popstate', handlePopState);
      onGeneratingChange?.(false);
    };
  }, [isGenerating, onGeneratingChange]);

  const initializeServices = async () => {
    try {
      const keys = await authenticatedRequest(() => apiClient.getApiKeys());
      
      if (!keys.openai && !keys.anthropic && !keys.fal && !keys.openrouter) {
        throw new Error("No API keys configured. Please add required API keys in settings.");
      }
  
      // Initialize image generation services
      if (keys.fal) {
        imageClient.setApiKeys(keys);
        console.log('Image generation services initialized');
      } else {
        console.warn('Fal.ai API key not configured - image generation will be disabled');
        setFeaturedImage(false);
      }
  
    } catch (error) {
      console.error("Failed to initialize services:", error);
      const errorMessage = error.message.includes('API keys') 
        ? error.message 
        : 'Failed to initialize services. Please check your API keys in settings.';
      setError(errorMessage);
    }
  };

const LeaveConfirmationDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Leave Page?</DialogTitle>
    <DialogContent>
      <Typography>
        Article is being generated. If you leave now, all progress will be lost.
      </Typography>
      <Typography color="error" sx={{ mt: 2 }}>
        Are you sure you want to leave this page?
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>
        Stay on Page
      </Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Leave Page
      </Button>
    </DialogActions>
  </Dialog>
);

const NavigationDialog = () => (
  <Dialog open={navDialogOpen} onClose={() => setNavDialogOpen(false)}>
    <DialogTitle>Leave Page?</DialogTitle>
    <DialogContent>
      <Typography>
        Article is being generated. If you leave now, all progress will be lost.
      </Typography>
      <Typography color="error" sx={{ mt: 2 }}>
        Are you sure you want to leave this page?
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setNavDialogOpen(false)}>
        Stay on Page
      </Button>
      <Button
        onClick={() => {
          setIsGenerating(false);
          setNavDialogOpen(false);
          window.history.back();
        }}
        color="error"
        variant="contained"
      >
        Leave Page
      </Button>
    </DialogActions>
  </Dialog>
);



  const fetchInitialData = async () => {
    localStorage.removeItem('availableModels');
   
    try {
      const [sitesData, promptsData] = await Promise.all([
        authenticatedRequest(() => apiClient.getWordPressSites()),
        authenticatedRequest(() => apiClient.getSavedPrompts())
      ]);
  
      setSites(sitesData);
      if (sitesData.length > 0) {
        setSelectedSite(sitesData[0].id);
      }
      
      // Update this part to handle the new prompt structure
      setPrompts({
        systemTemplates: promptsData.system_templates || [],
        userPrompts: promptsData.user_prompts || []
      });
    } catch (err) {
      setError('Failed to load initial data');
    }
  };

  const fetchUsers = async () => {
    if (!selectedSite) return;
    setLoadingUsers(true);
    try {
      const site = sites.find(s => s.id === selectedSite);
      if (!site) throw new Error('Selected site not found');
      
      const wpClient = new WordPressClient(site);
      const credentials = await authenticatedRequest(() => 
        apiClient.getWordPressCredentials(selectedSite)
      );
      wpClient.setCredentials(credentials.username, credentials.password);
      
      const usersData = await wpClient.getUsers();
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchCategories = async () => {
    if (!selectedSite) return;
    try {
      const categoriesData = await authenticatedRequest(() => 
        apiClient.getCategories(selectedSite)
      );
      setCategories(categoriesData);
    } catch (err) {
      setError('Failed to load categories');
    }
  };

  const handleGenerateSubheadings = async () => {
    if (!title.trim()) {
      setError('Please enter an article title first');
      return;
    }
  
    setIsGeneratingSubheadings(true);
    setError(null);
  
    try {
      if (!availableModels.length) {
        throw new Error('No AI models available');
      }
  
      const auxModel = getAuxiliaryModel();
      if (!auxModel) {
        throw new Error('No auxiliary model available');
      }
  
      const prompt = `Create 5-7 logical subheadings for an article titled "${title}". 
        The subheadings should:
        - Follow a natural progression
        - Cover the main aspects of the topic
        - Be clear and descriptive
        Return only the subheadings, one per line.`;
  
      console.log('Using auxiliary model:', auxModel);
      
      const response = await aiClient.generateArticleContent({
        title,
        modelId: auxModel,
        customPrompt: prompt
      });
  
      const newSubheadings = response
        .split('\n')
        .filter(line => line.trim())
        .map((text, index) => ({
          id: `subheading-${Date.now()}-${index}`,
          text: text.replace(/^#+ /, '').trim(),
          notes: '',
          wordCount: 200
        }));
  
      setSubheadings(currentSubheadings => [...currentSubheadings, ...newSubheadings]);
    } catch (err) {
      console.error('Subheading generation error:', err);
      setError('Failed to generate subheadings: ' + err.message);
    } finally {
      setIsGeneratingSubheadings(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(subheadings);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSubheadings(items);
  };

  const handleAddSubheading = () => {
    const newSubheading = {
      id: `subheading-${Date.now()}`,
      text: '',
      notes: '',
      wordCount: 200,
      generateImage: false,  
      imagePrompt: '',     
    };
    setSubheadings([...subheadings, newSubheading]);
    setExpandedSubheading(newSubheading.id);
  };

  const handleDeleteSubheading = (id) => {
    setSubheadings(subheadings.filter(sh => sh.id !== id));
  };

  const handleUpdateSubheading = (id, field, value) => {
    console.log('Updating subheading:', { id, field, value });
    setSubheadings(prevSubheadings => {
      const newSubheadings = prevSubheadings.map(sh => 
        sh.id === id ? { ...sh, [field]: value } : sh
      );
      console.log('Updated subheadings:', newSubheadings);
      return newSubheadings;
    });
  };
  
  // Generate SEO keywords based on title
  const handleGenerateKeywords = async () => {
    if (!title.trim()) {
      setError('Please enter a title before generating keywords');
      return;
    }
    
    setIsGeneratingKeywords(true);
    setError(null);
    
    try {
      // Use the selected model if available, otherwise use the default auxiliary model
      const auxModel = model || getAuxiliaryModel();
      if (!auxModel) {
        throw new Error('No model available for keyword generation');
      }
      
      const prompt = `Generate 15 SEO keywords and phrases for an article titled "${title}".
        Include a mix of:
        - Primary keywords (1-2 words)
        - Long-tail keywords (3-5 words)
        - Question-based keywords
        - Keywords with high search volume potential
        
        Return only the keywords, one per line, without numbering.`;
      
      const response = await aiClient.generateArticleContent({
        title: `Keywords for ${title}`,
        modelId: auxModel,
        customPrompt: prompt
      });
      
      const keywords = response
        .split('\n')
        .filter(line => line.trim())
        .map(keyword => {
          // Remove quotes, bullets or other formatting
          let cleanKeyword = keyword.replace(/^[-*•]?\s*/, '').trim();
          cleanKeyword = cleanKeyword.replace(/^["'](.*)["']$/, '$1').trim();
          return cleanKeyword;
        })
        .filter(keyword => keyword.length > 0);
      
      setGeneratedKeywords(keywords);
      setSelectedKeywords(keywords.length > 0 ? [keywords[0]] : []);
      setKeywordMode('generate');
    } catch (err) {
      console.error('Keyword generation error:', err);
      setError('Failed to generate keywords: ' + err.message);
    } finally {
      setIsGeneratingKeywords(false);
    }
  };
  
  // Handle toggling a keyword selection
  const handleKeywordToggle = (keyword) => {
    setSelectedKeywords(prev => {
      if (prev.includes(keyword)) {
        return prev.filter(k => k !== keyword);
      } else {
        return [...prev, keyword];
      }
    });
  };

  // Generate SEO metadata using AI
  const handleGenerateMetaFields = async () => {
    if (!title.trim()) {
      setError('Please enter a title before generating SEO metadata');
      return;
    }
    
    setIsGeneratingMetaFields(true);
    setError(null);
    
    try {
      // Use the selected model or auxiliary model
      const auxModel = model || getAuxiliaryModel();
      if (!auxModel) {
        throw new Error('No model available for SEO metadata generation');
      }
      
      // Get keywords to include in the prompt
      const keywords = keywordMode === 'manual' 
        ? seoKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
        : selectedKeywords;
      
      const keywordsText = keywords.length > 0 
        ? `The content focuses on these keywords: ${keywords.join(', ')}.` 
        : '';
      
      const prompt = `Create SEO metadata for an article titled "${title}". ${keywordsText}

Please provide:

1. SEO Title (50-60 characters): An engaging, keyword-rich title that differs from the main article title but captures its essence. Include primary keywords near the beginning.

2. Meta Description (150-160 characters): A compelling summary that encourages clicks while accurately representing the content. Include primary keywords naturally.

Format your response precisely as follows (no additional text):

SEO TITLE:
[Your SEO title here]

META DESCRIPTION:
[Your meta description here]`;
      
      const response = await aiClient.generateArticleContent({
        title: `SEO Metadata for ${title}`,
        modelId: auxModel,
        customPrompt: prompt
      });
      
      // Extract title and description from the AI response
      const seoTitleMatch = response.match(/SEO TITLE:\s*(.+?)(?:\n|$)/i);
      const metaDescMatch = response.match(/META DESCRIPTION:\s*(.+?)(?:\n|$)/i);
      
      if (seoTitleMatch && seoTitleMatch[1]) {
        setSeoTitle(seoTitleMatch[1].trim());
      }
      
      if (metaDescMatch && metaDescMatch[1]) {
        setMetaDescription(metaDescMatch[1].trim());
      }
      
      setGenerateMetaFields(true);
    } catch (err) {
      console.error('SEO metadata generation error:', err);
      setError('Failed to generate SEO metadata: ' + err.message);
    } finally {
      setIsGeneratingMetaFields(false);
    }
  };

  const handlePreviewPrompt = (promptId) => {
    const prompt = [
      ...prompts.systemTemplates,
      ...prompts.userPrompts
    ].find(p => p.id === promptId);
    
    if (prompt) {
      setPreviewPrompt(prompt);
      setPreviewOpen(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setLoading(true);
    setIsGenerating(true);
    setError(null);
  
    setGenerationSteps([
      { id: 'init', text: 'Preparing Content Framework', status: 'pending', icon: <Settings size={16} /> },
      { id: 'content', text: 'Crafting Article Content', status: 'pending', icon: <FileText size={16} /> },
      { id: 'images', text: 'Generating & Placing Images', status: 'pending', icon: <Image size={16} /> },
      { id: 'upload', text: 'Publishing to WordPress', status: 'pending', icon: <ArrowRight size={16} /> }
    ]);
  
    const updateStepStatus = (stepId, status, message = null, error = null) => {
      setGenerationSteps((prev) =>
        prev.map((step) =>
          step.id === stepId ? { ...step, status, message, error } : step
        )
      );
    };
  
    try {
      setGenerationStatus('Starting article generation...');
      updateStepStatus('init', 'in-progress', 'Setting up generation environment');
  
      const prompt = [...prompts.systemTemplates, ...prompts.userPrompts].find(
        (p) => p.id === selectedPrompt
      );
      if (!prompt) throw new Error('Selected prompt template not found');
  
      const createStructuredPrompt = (
        title,
        subheadings,
        promptTemplate,
        optimizeForSearch
      ) => {
        const subheadingsText = subheadings
          .map((sh, index) => {
            let section = `${index + 1}. ${sh.text}`;
            if (sh.notes) section += `\nNotes: ${sh.notes}`;
            section += `\nTarget word count: ${sh.wordCount} words`;
            return section;
          })
          .join('\n');
  
        const searchOptimizationInstructions = optimizeForSearch
          ? `
  Additional Search Optimization Requirements:
  - Identify and naturally incorporate semantic variations of the main topic
  - Structure content to address common user questions and search intents
  - Include relevant LSI (Latent Semantic Indexing) keywords naturally
  - Ensure comprehensive coverage of related subtopics
  - Structure headings and subheadings to target featured snippets
  - Include clear definitions and explanations of key concepts
  - Use natural language that matches search queries
  `
          : '';
          
        // Prepare SEO keywords instructions
        const keywordsToUse = keywordMode === 'manual' 
          ? seoKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
          : selectedKeywords;
          
        let seoInstructions = '';
        if (keywordsToUse.length > 0) {
          seoInstructions = `
  SEO Keywords:
  - Naturally incorporate the following keywords throughout the article:
  ${keywordsToUse.map(k => `  - "${k}"`).join('\n')}
  - Include primary keywords in the introduction and conclusion
  - Use some keywords in subheadings where appropriate
  - Maintain a natural flow - don't force keywords where they don't fit
  - Aim for keyword density of 1-2% for primary keywords
  `;
        }
  
        return `${promptTemplate.prompt_text}
  
  Article Title: ${title}
  
  Required Structure:
  ${subheadingsText}
  
  Additional Requirements:
  - Follow the exact subheading structure provided above
  - Maintain the specified word count for each section
  - Use the section notes as guidance for content
  ${searchOptimizationInstructions}
  ${seoInstructions}`;
      };
  
      updateStepStatus('init', 'completed', 'Framework prepared successfully');
      updateStepStatus('content', 'in-progress', 'Creating rich content for your article');
  
      const structuredPrompt = createStructuredPrompt(
        title,
        subheadings,
        prompt,
        optimizeForSearch
      );
  
      setGenerationStatus(`Generating article content for "${title}"`);
      const rawContent = await aiClient.generateArticleContent({
        title,
        modelId: model,
        customPrompt: structuredPrompt,
      });
  
      updateStepStatus('content', 'completed', 'Article content crafted successfully');
      
      // Auto-generate SEO metadata in the background if enabled
      if (autoGenerateSeo) {
        setGenerationStatus('Generating SEO metadata...');
        try {
          // Use the selected model or auxiliary model
          const auxModel = model || getAuxiliaryModel();
          
          // Get keywords to include in the prompt
          const keywords = keywordMode === 'manual' 
            ? seoKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
            : selectedKeywords;
          
          const keywordsText = keywords.length > 0 
            ? `The content focuses on these keywords: ${keywords.join(', ')}.` 
            : '';
          
          const prompt = `Create SEO metadata for an article titled "${title}". ${keywordsText}
            The article content is about: ${subheadings.map(sh => sh.text).join(', ')}.

            Please provide:

            1. SEO Title (50-60 characters): An engaging, keyword-rich title that differs from the main article title but captures its essence. Include primary keywords near the beginning.

            2. Meta Description (150-160 characters): A compelling summary that encourages clicks while accurately representing the content. Include primary keywords naturally.

            Format your response precisely as follows (no additional text):

            SEO TITLE:
            [Your SEO title here]

            META DESCRIPTION:
            [Your meta description here]`;
          
          const response = await aiClient.generateArticleContent({
            title: `SEO Metadata for ${title}`,
            modelId: auxModel,
            customPrompt: prompt
          });
          
          // Extract title and description from the AI response
          const seoTitleMatch = response.match(/SEO TITLE:\s*(.+?)(?:\n|$)/i);
          const metaDescMatch = response.match(/META DESCRIPTION:\s*(.+?)(?:\n|$)/i);
          
          if (seoTitleMatch && seoTitleMatch[1]) {
            setSeoTitle(seoTitleMatch[1].trim());
          }
          
          if (metaDescMatch && metaDescMatch[1]) {
            setMetaDescription(metaDescMatch[1].trim());
          }
          
          // Always enable SEO metadata inclusion in the post
          setGenerateMetaFields(true);
          
          console.log('Auto-generated SEO metadata:', {
            title: seoTitleMatch?.[1]?.trim(),
            description: metaDescMatch?.[1]?.trim()
          });
        } catch (seoError) {
          console.error('Error auto-generating SEO metadata:', seoError);
          // Continue with the rest of the process even if SEO generation fails
        }
      }
      
      updateStepStatus('images', 'in-progress', 'Processing and generating visuals');
  
      let featuredMediaId = null;
      let processedContent = formatContent(rawContent);
  
      const site = sites.find((s) => s.id === selectedSite);
      if (!site) throw new Error('Selected site not found');
  
      const wpClient = new WordPressClient(site);
      const credentials = await authenticatedRequest(() =>
        apiClient.getWordPressCredentials(selectedSite)
      );
      wpClient.setCredentials(credentials.username, credentials.password);
  
      // Handle featured image
      if (featuredImage) {
        setGenerationStatus(`Generating featured image for "${title}"`);
        updateStepStatus('images', 'in-progress', 'Creating eye-catching featured image');
        try {
          const imageOptions = {
            prompt: featuredImagePrompt || title,
            model: featuredImageModel,
            dimension: dimensions,  // This is correct now - matches the dimension prop name
            style: featuredImageStyle,
            enhancePrompt: !featuredImagePrompt,
            context: { articleTitle: title },
          };
  
          console.log('Featured image options:', imageOptions);
  
          const imageData = await imageClient.generateImage(imageOptions);
  
          if (imageData?.image_url) {
            setGenerationStatus('Uploading featured image...');
            const response = await fetch(imageData.image_url);
            const blob = await response.blob();
            const formData = new FormData();
            formData.append('file', blob, `featured-${Date.now()}.png`);
            formData.append('siteId', selectedSite);
  
            const uploadResult = await authenticatedRequest(() =>
              apiClient.uploadMedia(formData)
            );
            if (uploadResult?.media_id) {
              featuredMediaId = uploadResult.media_id;
            }
          }
        } catch (error) {
          console.error('Featured image error:', error);
          updateStepStatus('images', 'in-progress', 'Featured image generation failed, continuing with article');
          setGenerationStatus('Featured image generation failed, continuing...');
        }
      }
  
      // Process subheading images
      const hasImageSubheadings = subheadings.some((sh) => sh.generateImage);
      if (hasImageSubheadings) {
        console.log('Found subheadings with images, starting image generation...');
        setGenerationStatus('Processing subheading images...');
  
        const parser = new DOMParser();
        const doc = parser.parseFromString(processedContent, 'text/html');
        const contentHeadings = Array.from(doc.querySelectorAll('h2'));
        console.log('Found content headings:', contentHeadings.map((h) => h.textContent));
  
        const cleanText = (text) =>
          text
            .replace(/^[-#0-9.]+\s*/, '')
            .replace(/^[A-Z\s]+:\s*/, '')
            .replace(/:/g, '')
            .toLowerCase()
            .trim();
  
        for (const subheading of subheadings) {
          if (!subheading.generateImage) continue;
  
          const cleanedSubheadingText = cleanText(subheading.text);
          console.log('Looking for match for cleaned subheading:', cleanedSubheadingText);
  
          const matchingHeading = contentHeadings.find((h) => {
            const headingText = cleanText(h.textContent);
            return (
              headingText.includes(cleanedSubheadingText) ||
              cleanedSubheadingText.includes(headingText)
            );
          });
  
          if (matchingHeading) {
            try {
              const imageOptions = {
                prompt: subheading.imagePrompt || matchingHeading.textContent,
                model: subheadingImageModel,
                dimension: dimensions,  // This is correct now - matches the dimension prop name
                style: subheadingImageStyle,
                enhancePrompt: !subheading.imagePrompt,
                context: { 
                  articleTitle: title, 
                  sectionNotes: subheading.notes || '' 
                },
              };
  
              console.log(`Subheading image options for "${matchingHeading.textContent}":`, imageOptions);
  
              const imageData = await imageClient.generateImage(imageOptions);
  
              if (imageData?.image_url) {
                const response = await fetch(imageData.image_url);
                const blob = await response.blob();
                const formData = new FormData();
                formData.append('file', blob, `subheading-${Date.now()}.png`);
                formData.append('siteId', selectedSite);
  
                const uploadResult = await authenticatedRequest(() =>
                  apiClient.uploadMedia(formData)
                );
  
                if (uploadResult?.media_id) {
                  const img = doc.createElement('img');
                  img.src = uploadResult.url;
                  img.alt = matchingHeading.textContent;
                  img.className = `wp-image-${uploadResult.media_id}`;
                  img.style.cssText = 'max-width: 100%; height: auto; margin: 20px 0;';
                  matchingHeading.parentNode.insertBefore(img, matchingHeading.nextSibling);
                }
              }
            } catch (error) {
              console.error(`Failed to process image for "${matchingHeading.textContent}":`, error);
              setGenerationStatus(`Image generation failed for "${matchingHeading.textContent}", continuing...`);
            }
          } else {
            console.log(`No match found for subheading "${subheading.text}"`);
          }
        }
        processedContent = doc.body.innerHTML;
      }
  
      updateStepStatus('images', 'completed', 'Visual elements added successfully');
      updateStepStatus('upload', 'in-progress', 'Publishing to your WordPress site');
  
      // Final WordPress upload
      setGenerationStatus('Creating WordPress post...');
      let postData = {
        title,
        content: processedContent,
        status: 'draft',
      };
      
      // Add category if selected
      if (category) {
        postData.categoryId = parseInt(category, 10);
      }
      
      // Add featured image if available
      if (featuredMediaId) {
        postData.featuredMediaId = featuredMediaId;
      }
      
      // Add author if selected
      if (selectedUser) {
        postData.authorId = parseInt(selectedUser, 10);
      }
      
      // Prepare tags for WordPress from SEO keywords
      const tags = keywordMode === 'manual' 
        ? seoKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0)
        : selectedKeywords;
      
      if (tags.length > 0) {
        try {
          // Instead of directly passing tags as strings, we'll handle tag creation properly
          setGenerationStatus('Creating tags in WordPress...');
          
          // Some WordPress installations only accept pre-existing tag IDs
          // So we'll fetch all existing tags first
          const existingTagsResponse = await fetch(`${site.api_url}/tags?per_page=100`, {
            headers: {
              'Authorization': wpClient.getAuthHeader()
            }
          });
          
          if (!existingTagsResponse.ok) {
            throw new Error('Failed to fetch existing tags');
          }
          
          const existingTags = await existingTagsResponse.json();
          console.log('Existing tags:', existingTags);
          
          // Match keywords with existing tags
          const tagIds = [];
          for (const keyword of tags) {
            // Look for exact match in existing tags
            const matchingTag = existingTags.find(tag => 
              tag.name.toLowerCase() === keyword.toLowerCase()
            );
            
            if (matchingTag) {
              tagIds.push(matchingTag.id);
            } else {
              // If no match found, try to create new tag
              try {
                const newTag = await wpClient.createTag(keyword);
                if (newTag && newTag.id) {
                  tagIds.push(newTag.id);
                }
              } catch (createError) {
                console.warn(`Could not create tag "${keyword}":`, createError);
              }
            }
          }
          
          if (tagIds.length > 0) {
            // Make sure all IDs are integers
            postData.tags = tagIds.map(id => parseInt(id, 10));
            console.log('Using WordPress tag IDs:', postData.tags);
          }
        } catch (tagError) {
          console.error('Error handling tags:', tagError);
          // Continue with post creation but without tags
          delete postData.tags;
        }
      }
      
      // Add SEO metadata if available
      if (generateMetaFields && (seoTitle || metaDescription)) {
        // Initialize meta object if not present
        if (!postData.meta) {
          postData.meta = {};
        }
        
        // Add Yoast SEO metadata
        // These are the common meta fields for Yoast SEO plugin
        if (seoTitle) {
          postData.meta['_yoast_wpseo_title'] = seoTitle;
        }
        
        if (metaDescription) {
          postData.meta['_yoast_wpseo_metadesc'] = metaDescription;
        }
        
        // If there are keywords, set the first one as the focus keyword
        if (tags.length > 0) {
          const focusKeyword = keywordMode === 'manual' 
            ? seoKeywords.split(',')[0].trim() 
            : selectedKeywords[0];
            
          if (focusKeyword) {
            postData.meta['_yoast_wpseo_focuskw'] = focusKeyword;
          }
        }
        
        console.log('Adding SEO metadata:', {
          title: seoTitle,
          description: metaDescription,
          meta: postData.meta
        });
      }

      const post = await wpClient.createPost(postData);
  
      updateStepStatus('upload', 'completed', 'Successfully published as draft in WordPress');
      setGenerationStatus('✓ Article published successfully!');
  
      setGeneratedArticle({
        title,
        content: processedContent,
        featuredImage: featuredMediaId ? await wpClient.getMediaUrl(featuredMediaId) : null,
        link: post.link,
        postId: post.id,
      });
  
      setResponseDialogOpen(true);
      setError(null);
    } catch (err) {
      console.error('Error generating article:', err);
      const failedStep = generationSteps.find((step) => step.status === 'pending' || step.status === 'in-progress');
      if (failedStep) {
        updateStepStatus(failedStep.id, 'error', `Error: ${err.message}`, err.message);
      }
  
      setError(err.message || 'Failed to generate article');
      setGenerationStatus('Generation failed. Please check the error message and try again.');
      setResponseDialogOpen(true);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setIsGenerating(false);
      }, 1000); // Add a small delay to ensure all UI updates are complete
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {/* Page Header with Help Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        px: 3,  // Increased padding to move button away from edge
        pb: 2,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '96%',
          height: '1px',
          background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.01), rgba(59, 130, 246, 0.2) 20%, rgba(59, 130, 246, 0.2) 80%, rgba(59, 130, 246, 0.01))',
          borderRadius: '1px',
        }
      }}>
        <PageHeader 
          category="Content Creation"
          title="Create In-Depth Article"
          description="Create structured, AI-powered articles with customizable sections, images, and formatting. Perfect for producing well-organized, professional content for your WordPress site."
          icon={<FileText size={24} color="#E04C16" />} 
        />
        <Box sx={{ mr: 6 }}> {/* Added margin to move button left */}
        <Button
          onClick={() => setHelpDialogOpen(true)}
          startIcon={<HelpCircle size={18} />}
          sx={{
              bgcolor: 'rgba(224, 76, 22, 0.08)',
              color: '#E04C16',
              borderRadius: '12px',
              px: 3.5,
              py: 1,
              fontWeight: 600,
              fontSize: '0.875rem',
              minWidth: '160px',
              whiteSpace: 'nowrap',
              border: '1px solid rgba(224, 76, 22, 0.12)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease-in-out',
            '&:hover': {
                bgcolor: 'rgba(224, 76, 22, 0.12)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(224, 76, 22, 0.12)'
            }
          }}
        >
          How to Use
        </Button>
        </Box>
      </Box>
      
      {/* LoadingOverlay */}
      <LoadingOverlay visible={loading} status={generationStatus} steps={generationSteps} />
      
      {/* Main Content */}
      <Grid container spacing={3} sx={{ px: 2 }}>
        {/* Left Column - Article Setup */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(to bottom, #FFFFFF, #FAFBFF)'
          }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                {/* Article Title */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Article Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isGenerating}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          transition: 'all 0.2s ease'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(59, 130, 246, 0.5)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        '&.Mui-focused': {
                          color: '#3B82F6'
                        }
                      }
                    }}
                  />
                </Grid>

                {/* WordPress Site Selector */}
  <Grid item xs={12}>
    <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, width: '100%' }}>
      <Box sx={{ flexGrow: 1 }}>
        <FormControl fullWidth required>
                        <InputLabel sx={{ 
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: '#3B82F6'
                          }
                        }}>WordPress Site</InputLabel>
          <Select
            value={selectedSite}
            label="WordPress Site"
            onChange={(e) => setSelectedSite(e.target.value)}
            disabled={loading}
                          sx={{
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.12)',
                              transition: 'all 0.2s ease'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(59, 130, 246, 0.5)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3B82F6',
                              borderWidth: '1px'
                            },
                            '& .MuiSelect-select': {
                              padding: '14px 16px'
                            }
                          }}
          >
            {sites.map((site) => (
                            <MenuItem 
                              key={site.id} 
                              value={site.id}
                              sx={{
                                py: 1.5,
                                '&:hover': {
                                  bgcolor: 'rgba(59, 130, 246, 0.04)'
                                },
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(59, 130, 246, 0.08)',
                                  '&:hover': {
                                    bgcolor: 'rgba(59, 130, 246, 0.12)'
                                  }
                                }
                              }}
                            >
                {site.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Tooltip 
        title="Select the WordPress site where you want to publish this article." 
        placement="right"
        sx={{ mt: 1.5 }}
      >
                      <IconButton size="small" sx={{
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.08)'
                        }
                      }}>
                        <HelpCircle size={16} />
      </IconButton>
      </Tooltip>
    </Box>
  </Grid>
 
                {/* Category Selector */}
  <Grid item xs={12}>
        <FormControl fullWidth required>
                    <InputLabel sx={{ 
                      fontWeight: 500,
                      '&.Mui-focused': {
                        color: '#3B82F6'
                      }
                    }}>Category</InputLabel>
          <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                      disabled={loading || !selectedSite}
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          transition: 'all 0.2s ease'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(59, 130, 246, 0.5)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px'
                        },
                        '& .MuiSelect-select': {
                          padding: '14px 16px'
                        }
                      }}
                    >
                      {categories.map((cat) => (
                <MenuItem 
                          key={cat.id} 
                          value={cat.id}
                  sx={{
                            py: 1.5,
                            '&:hover': {
                              bgcolor: 'rgba(59, 130, 246, 0.04)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(59, 130, 246, 0.08)',
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.12)'
                              }
                            }
                          }}
                        >
                          {cat.name}
                </MenuItem>
                      ))}
          </Select>
        </FormControl>
  </Grid>

                {/* WordPress User Selector */}
  <Grid item xs={12}>
        <FormControl fullWidth required>
                    <InputLabel sx={{ 
                      fontWeight: 500,
                      '&.Mui-focused': {
                        color: '#3B82F6'
                      }
                    }}>WordPress User</InputLabel>
          <Select
                      value={selectedUser}
                      label="WordPress User"
                      onChange={(e) => setSelectedUser(e.target.value)}
                      disabled={loading || !selectedSite || loadingUsers}
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          transition: 'all 0.2s ease'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(59, 130, 246, 0.5)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px'
                        },
                        '& .MuiSelect-select': {
                          padding: '14px 16px'
                        }
                      }}
                    >
                      {users.map((user) => (
                        <MenuItem 
                          key={user.id} 
                          value={user.id}
                          sx={{
                            py: 1.5,
                            '&:hover': {
                              bgcolor: 'rgba(59, 130, 246, 0.04)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(59, 130, 246, 0.08)',
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.12)'
                              }
                            }
                          }}
                        >
                          {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
  </Grid>

                {/* Model Selector */}
  <Grid item xs={12}>
      <ModelSelector
        value={model}
        onChange={(e) => setModel(e.target.value)}
        required
                    tooltip="Select the AI model to use for generating your article content"
                    label="Writing Style Template"
                    showDescription
                    disabled={isGenerating || loading}
      />
      {modelsError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
          {modelsError}
                    </Alert>
      )}
</Grid>

                {/* Writing Style Template */}
<Grid item xs={12}>
      <FormControl fullWidth required>
                    <InputLabel sx={{ 
                      fontWeight: 500,
                      '&.Mui-focused': {
                        color: '#3B82F6'
                      }
                    }}>Writing Style Template</InputLabel>
  <Select
    value={selectedPrompt}
    label="Writing Style Template"
    onChange={(e) => setSelectedPrompt(e.target.value)}
                      disabled={isGenerating}
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          transition: 'all 0.2s ease'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(59, 130, 246, 0.5)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3B82F6',
                          borderWidth: '1px'
                        },
                        '& .MuiSelect-select': {
                          padding: '14px 16px'
                        }
                      }}
                    >
                      <ListSubheader sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#3B82F6',
                        bgcolor: 'rgba(59, 130, 246, 0.04)'
                      }}>
                        System Templates
                      </ListSubheader>
                      {prompts.systemTemplates.map((prompt) => (
        <MenuItem 
          key={prompt.id} 
          value={prompt.id}
                          sx={{
                            py: 1.5,
                            '&:hover': {
                              bgcolor: 'rgba(59, 130, 246, 0.04)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(59, 130, 246, 0.08)',
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.12)'
                              }
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {prompt.name}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewPrompt(prompt.id);
                              }}
                              sx={{
                                color: 'rgba(0, 0, 0, 0.54)',
                                '&:hover': {
                                  bgcolor: 'rgba(59, 130, 246, 0.08)'
                                }
                              }}
                            >
                              <Eye size={16} />
                            </IconButton>
          </Box>
        </MenuItem>
                      ))}
                      
                      <ListSubheader sx={{ 
                        fontSize: '0.875rem',
          fontWeight: 600,
                        color: '#3B82F6',
                        bgcolor: 'rgba(59, 130, 246, 0.04)'
                      }}>
                        Custom Templates
                      </ListSubheader>
                      {prompts.userPrompts.map((prompt) => (
                        <MenuItem 
                          key={prompt.id} 
                          value={prompt.id}
                          sx={{
                            py: 1.5,
                            '&:hover': {
                              bgcolor: 'rgba(59, 130, 246, 0.04)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(59, 130, 246, 0.08)',
                              '&:hover': {
                                bgcolor: 'rgba(59, 130, 246, 0.12)'
                              }
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          {prompt.name}
          <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewPrompt(prompt.id);
                              }}
                              sx={{
                                color: 'rgba(0, 0, 0, 0.54)',
                                '&:hover': {
                                  bgcolor: 'rgba(59, 130, 246, 0.08)'
                                }
                              }}
                            >
                              <Eye size={16} />
          </IconButton>
      </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
</Grid>
  
                {/* Subheading Controls */}
  <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mb: 3,
                    '& .MuiButton-root': {
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      padding: '10px 20px',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: 'none'
                    }
                  }}>
                    <Button
                      onClick={handleGenerateSubheadings}
                      startIcon={<Wand2 />}
                      variant="contained"
                      disabled={isGenerating || !title || isGeneratingSubheadings}
                      sx={{ 
                        flexGrow: 1,
                        bgcolor: '#3B82F6',
                        '&:hover': {
                          bgcolor: '#2563EB',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                        },
                        '&:disabled': {
                          bgcolor: 'rgba(59, 130, 246, 0.12)',
                          color: 'rgba(59, 130, 246, 0.5)'
                        }
                      }}
                    >
                      {isGeneratingSubheadings ? 'Generating...' : 'Generate Subheadings'}
                    </Button>
                    <Button
                      disabled={isGenerating}
                      onClick={handleAddSubheading}
                      startIcon={<Plus />}
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        color: '#3B82F6',
                        '&:hover': {
                          borderColor: '#3B82F6',
                          bgcolor: 'rgba(59, 130, 246, 0.04)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Add Custom
                    </Button>
                    <Button
                      disabled={isGenerating || subheadings.length === 0}
                      onClick={() => setSubheadings([])}
                      startIcon={<Trash2 />}
                      variant="outlined"
                      color="error"
                      sx={{
                        borderColor: 'rgba(239, 68, 68, 0.5)',
                        color: '#EF4444',
                        '&:hover': {
                          borderColor: '#EF4444',
                          bgcolor: 'rgba(239, 68, 68, 0.04)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Clear All
                    </Button>
    </Box>
                </Grid>

                {/* Settings Section */}
                <Grid item xs={12}>
                  <Paper sx={{ 
                    p: 2.5, 
                    mb: 2,
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    background: 'linear-gradient(to right, rgba(59, 130, 246, 0.03), rgba(255, 255, 255, 0.5))',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
                  }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: 2, 
                      fontWeight: 600, 
                      color: '#1F2937',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Settings size={18} color="#3B82F6" />
                      Advanced Settings
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 2
                    }}>
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
                        justifyContent: 'space-between',
      p: 1.5,
                        borderRadius: '10px',
                        bgcolor: optimizeForSearch ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.8)',
      border: '1px solid',
                        borderColor: optimizeForSearch ? 'rgba(59, 130, 246, 0.2)' : 'rgba(0, 0, 0, 0.06)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: optimizeForSearch ? 'rgba(59, 130, 246, 0.12)' : 'rgba(255, 255, 255, 0.9)',
                          borderColor: optimizeForSearch ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.1)'
                        }
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1F2937' }}>
                              Optimize for Search Intent
                            </Typography>
            <Tooltip 
              title="When enabled, the AI will structure content to better match search intent and include relevant semantic variations of key topics." 
                              placement="top"
            >
                              <HelpCircle size={16} color="#6B7280" />
            </Tooltip>
          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                            Enhances SEO by targeting search queries and user intent
                          </Typography>
                        </Box>
                        <Switch
                          checked={optimizeForSearch}
                          onChange={(e) => setOptimizeForSearch(e.target.checked)}
                          disabled={isGenerating || loading}
                          sx={{
                            '& .MuiSwitch-switchBase': {
                              '&.Mui-checked': {
                                color: '#3B82F6',
                                '& + .MuiSwitch-track': {
                                  backgroundColor: '#3B82F6',
                                  opacity: 0.5
                                }
                              }
                            },
                            '& .MuiSwitch-track': {
                              borderRadius: '10px'
                            },
                            '& .MuiSwitch-thumb': {
                              boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)'
                            }
                          }}
      />
    </Box>
    
    {/* SEO Keywords Section */}
    <Box sx={{
      p: 1.5,
      borderRadius: '10px',
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(0, 0, 0, 0.1)'
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1F2937' }}>
          SEO Keywords
        </Typography>
        <Tooltip 
          title="Specify keywords to be naturally incorporated throughout the article. These will also be added as tags in WordPress." 
          placement="top"
        >
          <HelpCircle size={16} color="#6B7280" />
        </Tooltip>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <Button
          variant={keywordMode === 'manual' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setKeywordMode('manual')}
          sx={{ 
            minWidth: '90px',
            bgcolor: keywordMode === 'manual' ? '#3B82F6' : 'transparent',
            '&:hover': {
              bgcolor: keywordMode === 'manual' ? '#2563EB' : 'rgba(59, 130, 246, 0.04)'
            }
          }}
        >
          Manual
        </Button>
        <Button
          variant={keywordMode === 'generate' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setKeywordMode('generate')}
          sx={{ 
            minWidth: '90px',
            bgcolor: keywordMode === 'generate' ? '#3B82F6' : 'transparent',
            '&:hover': {
              bgcolor: keywordMode === 'generate' ? '#2563EB' : 'rgba(59, 130, 246, 0.04)'
            }
          }}
        >
          Generate
        </Button>
      </Box>
      
      {keywordMode === 'manual' ? (
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Enter keywords separated by commas"
          value={seoKeywords}
          onChange={(e) => setSeoKeywords(e.target.value)}
          disabled={loading || isGenerating}
          placeholder="e.g., wordpress, seo, content marketing"
        />
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerateKeywords}
              disabled={!title || loading || isGenerating || isGeneratingKeywords}
              startIcon={isGeneratingKeywords ? <CircularProgress size={16} /> : <Wand2 size={16} />}
              sx={{
                borderColor: 'rgba(59, 130, 246, 0.5)',
                color: '#3B82F6',
                '&:hover': {
                  borderColor: '#3B82F6',
                  bgcolor: 'rgba(59, 130, 246, 0.04)'
                }
              }}
            >
              {isGeneratingKeywords ? 'Generating...' : 'Generate Keywords'}
            </Button>
            {selectedKeywords.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {selectedKeywords.length} selected
              </Typography>
            )}
          </Box>
          
          {generatedKeywords.length > 0 && (
            <Box sx={{ 
              maxHeight: '150px', 
              overflowY: 'auto', 
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'white'
            }}>
              {generatedKeywords.map((keyword, index) => (
                <Chip
                  key={index}
                  label={keyword}
                  size="small"
                  onClick={() => handleKeywordToggle(keyword)}
                  onDelete={selectedKeywords.includes(keyword) ? () => handleKeywordToggle(keyword) : undefined}
                  color={selectedKeywords.includes(keyword) ? "primary" : "default"}
                  variant={selectedKeywords.includes(keyword) ? "filled" : "outlined"}
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
    
    {/* Auto-Generate SEO Metadata Switch */}
    <Box sx={{
      p: 1.5,
      mt: 2,
      borderRadius: '10px',
      bgcolor: autoGenerateSeo ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.8)',
      border: '1px solid',
      borderColor: autoGenerateSeo ? 'rgba(59, 130, 246, 0.2)' : 'rgba(0, 0, 0, 0.06)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        bgcolor: autoGenerateSeo ? 'rgba(59, 130, 246, 0.12)' : 'rgba(255, 255, 255, 0.9)',
        borderColor: autoGenerateSeo ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.1)'
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1F2937' }}>
              Auto-Generate SEO Metadata
            </Typography>
            <Tooltip 
              title="Automatically generate SEO title and meta description during article creation" 
              placement="top"
            >
              <HelpCircle size={16} color="#6B7280" />
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
            AI will create optimized metadata using your article content
          </Typography>
        </Box>
        <Switch
          checked={autoGenerateSeo}
          onChange={(e) => {
            const newValue = e.target.checked;
            setAutoGenerateSeo(newValue);
            // If turning on auto-generation, also ensure the meta fields get included
            if (newValue) {
              setGenerateMetaFields(true);
            }
          }}
          disabled={isGenerating || loading}
          sx={{
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                color: '#3B82F6',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#3B82F6',
                  opacity: 0.5
                }
              }
            },
            '& .MuiSwitch-track': {
              borderRadius: '10px'
            },
            '& .MuiSwitch-thumb': {
              boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)'
            }
          }}
        />
      </Box>
      
      {/* Show status message based on toggle state */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        {autoGenerateSeo ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            py: 1,
            px: 2,
            borderRadius: '8px',
            bgcolor: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <CheckCircle size={16} color="#10B981" />
            <Typography variant="body2" color="#10B981">
              SEO title and meta description will be automatically generated
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            py: 1,
            px: 2,
            borderRadius: '8px',
            bgcolor: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            <AlertCircle size={16} color="#EF4444" />
            <Typography variant="body2" color="#EF4444">
              SEO metadata will not be automatically generated
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  </Box>
                  </Paper>
</Grid>
                             
                {/* Image Settings */}
<Grid item xs={12}>
                  <Paper sx={{ 
                    p: 2.5, 
                    mb: 2,
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    background: 'linear-gradient(to right, rgba(236, 72, 153, 0.03), rgba(255, 255, 255, 0.5))',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
                  }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: 2, 
                      fontWeight: 600, 
                      color: '#1F2937',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Image size={18} color="#EC4899" />
                      Image Settings
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: '10px',
                      bgcolor: featuredImage ? 'rgba(236, 72, 153, 0.08)' : 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid',
                      borderColor: featuredImage ? 'rgba(236, 72, 153, 0.2)' : 'rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: featuredImage ? 'rgba(236, 72, 153, 0.12)' : 'rgba(255, 255, 255, 0.9)',
                        borderColor: featuredImage ? 'rgba(236, 72, 153, 0.3)' : 'rgba(0, 0, 0, 0.1)'
                      }
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1F2937' }}>
                            Generate Featured Image
                          </Typography>
                          <Tooltip title="AI will create a featured image for your article based on the title or your custom prompt">
                            <HelpCircle size={16} color="#6B7280" />
                          </Tooltip>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          Creates eye-catching header image using AI
                        </Typography>
                      </Box>
                      <Switch
          checked={featuredImage}
          onChange={(e) => setFeaturedImage(e.target.checked)}
          disabled={isGenerating || loading}
          sx={{
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                              color: '#EC4899',
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#EC4899',
                                opacity: 0.5
                              }
                            }
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: '10px'
                          },
                          '& .MuiSwitch-thumb': {
                            boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)'
                          }
                        }}
                      />
  </Box>
                  </Paper>
                </Grid>
  
  {featuredImage && (
      <Grid item xs={12}>
                    <Box sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      p: 2.5,
                      borderRadius: '12px',
                      border: '1px solid rgba(236, 72, 153, 0.15)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
                      background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.9), rgba(236, 72, 153, 0.05))'
                    }}>
                      <ImageControls
                        model={featuredImageModel}
                        setModel={setFeaturedImageModel}
                        style={featuredImageStyle}
                        setStyle={setFeaturedImageStyle}
                        dimension={dimensions}
                        setDimension={setDimensions}
                        disabled={isGenerating || loading}
                      />
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Featured Image Prompt (optional)"
          placeholder="Leave blank to let AI generate based on article title and content"
          value={featuredImagePrompt}
          onChange={(e) => setFeaturedImagePrompt(e.target.value)}
          disabled={isGenerating}
                          sx={{ 
                          mt: 2,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            '& fieldset': {
                              borderColor: 'rgba(236, 72, 153, 0.2)',
                              transition: 'all 0.2s ease'
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(236, 72, 153, 0.5)'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#EC4899',
                              borderWidth: '1px'
                            }
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: '#EC4899'
                            }
                          }
                        }}
                      />
                                                  </Box>
                </Grid>
                )}

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isGenerating || !title || subheadings.length === 0 || loading}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        padding: '12px 24px',
                        background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #2563EB, #1D4ED8)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)'
                        },
                        '&:disabled': {
                          background: 'linear-gradient(45deg, #E5E7EB, #F3F4F6)',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={20} sx={{ color: 'inherit' }} />
                          <span>Generating Article...</span>
                        </Box>
                      ) : (
                        'Generate Article'
                      )}
                  </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to reset? All your changes will be lost.')) {
                          window.location.reload();
                        }
                      }}
                      disabled={isGenerating || loading}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        padding: '12px 24px',
                        borderColor: 'rgba(239, 68, 68, 0.5)',
                        color: '#EF4444',
                        minWidth: '120px',
                        '&:hover': {
                          borderColor: '#EF4444',
                          bgcolor: 'rgba(239, 68, 68, 0.04)',
                          transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                          color: 'rgba(0, 0, 0, 0.26)'
                        }
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

       {/* Right Column - Preview & Status */}
<Grid item xs={12} md={6}>
  <Paper sx={{ 
    p: 3, 
    height: '100%',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    background: 'linear-gradient(to bottom, #FFFFFF, #F8FAFC)',
    position: 'sticky',
    top: '24px'
  }}>
    <Typography 
      variant="h6" 
      gutterBottom 
      sx={{ 
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#1F2937',
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&::before': {
          content: '""',
          width: '3px',
          height: '18px',
          backgroundColor: '#E04C16',
          borderRadius: '2px',
          display: 'inline-block'
        }
      }}
    >
      Article Structure Preview
    </Typography>
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="h6" 
        color="primary" 
        gutterBottom 
        sx={{ 
          mb: 3,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#1F2937',
          lineHeight: 1.3
        }}
      >
        {title || 'Your Article Title'}
      </Typography>
      {subheadings.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="subheadings">
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
          {subheadings.map((subheading, index) => (
                  <Draggable 
              key={subheading.id} 
                    draggableId={subheading.id} 
                    index={index}
                  >
                    {(provided) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
              sx={{ 
                          px: 2,
                          py: 2,
                mb: 2,
                          display: 'block',
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '8px',
                          border: '1px solid rgba(0, 0, 0, 0.04)',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 1,
                          mb: 1
                        }}>
                          <Box {...provided.dragHandleProps}>
                            <GripVertical size={16} color="#9CA3AF" />
                          </Box>
                          <TextField
                            fullWidth
                            value={subheading.text}
                            onChange={(e) => handleUpdateSubheading(subheading.id, 'text', e.target.value)}
                            placeholder="Enter subheading text"
                            variant="standard"
                            sx={{
                              '& .MuiInput-root': {
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                color: '#1F2937'
                              }
                            }}
                          />
                          <IconButton
                            onClick={() => {
                              const newGenerateImage = !subheading.generateImage;
                              handleUpdateSubheading(subheading.id, 'generateImage', newGenerateImage);
                              if (newGenerateImage) {
                                setExpandedSubheading(subheading.id);
                              }
                            }}
                            size="small"
                            sx={{
                              color: subheading.generateImage ? '#3B82F6' : '#9CA3AF',
                              bgcolor: subheading.generateImage ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                              '&:hover': {
                                bgcolor: subheading.generateImage ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)'
                              }
                            }}
                          >
                            <Image size={16} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteSubheading(subheading.id)}
                            size="small"
                            sx={{
                              color: '#EF4444',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.08)'
                              }
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                        
                        <Collapse in={expandedSubheading === subheading.id}>
                          <Box sx={{ pl: 4, mt: 2 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              label="Section Notes"
                              value={subheading.notes}
                              onChange={(e) => handleUpdateSubheading(subheading.id, 'notes', e.target.value)}
                              placeholder="Add notes or instructions for this section"
                              sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                  fontSize: '0.875rem'
                                }
                              }}
                            />
                            
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: 2,
                              mb: 2
                            }}>
                              <TextField
                                type="number"
                                label="Word Count"
                                value={subheading.wordCount}
                                onChange={(e) => handleUpdateSubheading(subheading.id, 'wordCount', parseInt(e.target.value) || 0)}
                                sx={{
                                  width: '150px',
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '0.875rem'
                                  }
                                }}
                              />
                            </Box>
                            
                            {subheading.generateImage && (
                              <Box sx={{ pl: 2 }}>
                                <ImageControls
                                  model={subheadingImageModel}
                                  setModel={setSubheadingImageModel}
                                  style={subheadingImageStyle}
                                  setStyle={setSubheadingImageStyle}
                                  dimension={dimensions}
                                  setDimension={setDimensions}
                                  disabled={isGenerating || loading}
                                />
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  label="Image Prompt (optional)"
                                  value={subheading.imagePrompt || ''}
                                  onChange={(e) => handleUpdateSubheading(subheading.id, 'imagePrompt', e.target.value)}
                                  placeholder="Leave blank to let AI generate based on section content"
                                  sx={{
                                    mt: 2,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      fontSize: '0.875rem'
                                    }
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                        
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mt: 1
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: 'rgba(224, 76, 22, 0.1)',
                            color: '#E04C16',
                            px: 1,
                            py: 0.5,
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            {subheading.wordCount} words
                          </Box>
                {subheading.generateImage && (
                            <Box sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              color: '#3B82F6',
                              px: 1,
                              py: 0.5,
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}>
                              <Image size={12} />
                              {subheading.imagePrompt ? 'Custom image' : 'AI image'}
                            </Box>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => setExpandedSubheading(
                              expandedSubheading === subheading.id ? null : subheading.id
                            )}
                            sx={{
                              ml: 'auto',
                              color: '#6B7280',
                              '&:hover': {
                                bgcolor: 'rgba(107, 114, 128, 0.08)'
                              }
                            }}
                          >
                            {expandedSubheading === subheading.id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </IconButton>
                        </Box>
            </ListItem>
                    )}
                  </Draggable>
          ))}
                {provided.placeholder}
        </List>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Typography 
          color="text.secondary" 
          sx={{ fontSize: '1rem' }}
        >
          No subheadings added yet. Use the generate button or add custom subheadings.
        </Typography>
      )}
    </Box>
  </Paper>
</Grid>

<Dialog 
  open={helpDialogOpen}
  onClose={() => setHelpDialogOpen(false)}
  maxWidth="md"
  PaperProps={{
    sx: {
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    }
  }}
>
  <DialogTitle sx={{ 
    pb: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5
  }}>
  <Box sx={{ 
      width: 40, 
      height: 40, 
    display: 'flex', 
    alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '10px',
      background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
    }}>
      <HelpCircle size={24} />
    </Box>
    <Typography variant="h6">How to Use Single Article Creator</Typography>
    </DialogTitle>
  <IconButton
    aria-label="close"
    onClick={() => setHelpDialogOpen(false)}
    sx={{
      position: 'absolute',
      right: 16,
      top: 16,
      color: 'text.secondary',
    }}
  >
    <X size={20} />
    </IconButton>
  <DialogContent sx={{ pt: 2 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
     Getting Started
   </Typography>
        <Typography variant="body2" color="text.secondary">
     Welcome to the article creation tool! This page lets you build a structured article using AI-powered features or your own custom inputs. Start by entering an article title, then choose to either generate AI-suggested subheadings or create your own custom sections. You can also add images—either AI-generated using different quality models or with your own prompts—to enhance your article. Customize everything to fit your vision before generating the final piece!
   </Typography>
      </Box>
 
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
     Creating Your Article Structure
   </Typography>
        <Typography variant="body2" color="text.secondary">
          Follow these steps to build your article's foundation:
   </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Enter your article title:</strong> Type a clear, specific title in the title field (e.g., "The Benefits of Remote Work"). This sets the focus for your article and guides AI suggestions.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Generate subheadings or add custom ones:</strong> Click "Generate Subheadings" to let the AI suggest a structure based on your title (powered by the auxiliary AI model selected in the sidebar), or manually add your own sections by clicking "Add Section."
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Reorder sections:</strong> Use the drag-and-drop feature to rearrange subheadings in a logical order that flows well for readers.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Add notes and word count targets:</strong> For each section, include notes to guide the AI (e.g., "Mention productivity stats") and set a target word count (e.g., 200 words) to control length.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
       <strong>Customize with images:</strong> Enable images for the featured section or specific subheadings by clicking the image icon to the right of each subheading. Expand the section to choose from different image models (cheaper models may produce lower-quality images) or enter your own prompt. AI-generated image prompts use the auxiliary AI model from the sidebar.
   </Typography>
        </Box>
      </Box>
 
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
     Customizing Sections
   </Typography>
        <Typography variant="body2" color="text.secondary">
     Fine-tune each section to match your vision:
   </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Edit subheading text:</strong> Click the subheading text to modify it directly (e.g., change "Introduction" to "Why Remote Work Matters").
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Add notes:</strong> Use the notes field to provide specific instructions for the AI, like "Include a real-world example" or "Focus on environmental benefits."
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Set target word counts:</strong> Enter a number (e.g., 150 or 300 words) to ensure each section meets your desired length.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Enable images:</strong> Click the image icon next to a subheading to add an image, then expand the section to select an image model or enter a custom prompt. Higher-quality models cost more but deliver better results.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Add or remove sections:</strong> Click "Add Section" to expand your article or the trash icon to delete unnecessary ones.
   </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            <strong>Reorder with drag and drop:</strong> Adjust the sequence by dragging sections up or down to improve the article's flow.
          </Typography>
        </Box>
      </Box>
 
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#E04C16' }}>
     Best Practices for Great Results
   </Typography>
        <Typography variant="body2" color="text.secondary">
          Maximize the tool's potential with these tips:
   </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Use clear, descriptive subheadings:</strong> Instead of "Overview," try "Key Advantages of Solar Energy" to guide the AI and engage readers.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Balance section lengths:</strong> Aim for consistent word counts (e.g., 200-300 words per section) to maintain a smooth reading experience.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Add specific notes:</strong> Include details like "Cite a study" or "Use a positive tone" to shape the AI's output more precisely.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Choose image models wisely:</strong> Select a higher-quality image model for professional results, especially for featured images. Cheaper models are faster but may lack detail or accuracy.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Select the right auxiliary AI model:</strong> Subheading generation and AI image prompts rely on the auxiliary AI model chosen in the sidebar. Pick a capable model for better suggestions and relevant images.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
       <strong>Review the structure preview:</strong> Before generating the full article, check the preview to ensure the layout, details, and image settings align with your goals.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
       <strong>Use AI suggestions as a starting point:</strong> Let the AI generate subheadings or images, then tweak them to fit your unique voice or needs.
   </Typography>
        </Box>
      </Box>
    </Box>
 </DialogContent>
  <DialogActions sx={{ px: 3, pb: 3 }}>
    <Button 
      onClick={() => setHelpDialogOpen(false)}
      variant="contained"
      sx={{
        borderRadius: '10px',
        textTransform: 'none',
        fontWeight: 600,
        background: 'linear-gradient(45deg, #E04C16, #FF6B3D)',
        boxShadow: '0 4px 12px rgba(224, 76, 22, 0.3)',
        '&:hover': {
          background: 'linear-gradient(45deg, #CC4414, #E85E35)',
        }
      }}
    >
      Got it
    </Button>
  </DialogActions>
      </Dialog>

     {/* Preview Dialog */}
     <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewPrompt?.name}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            {previewPrompt?.prompt_text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Grid>
      <ArticleResponseDialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        success={!!generatedArticle}
        error={error}
        articleData={generatedArticle}
        onPreview={() => {
          setResponseDialogOpen(false);
          setPreviewDialogOpen(true);
        }}
      />

      <ArticlePreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        article={generatedArticle}
      />
       <LeaveConfirmationDialog
      open={leaveDialogOpen}
      onClose={() => setLeaveDialogOpen(false)}
      onConfirm={() => {
        setIsGenerating(false);
        setLeaveDialogOpen(false);
        if (navigationTarget) {
          window.location.href = navigationTarget;
        }
      }}
    />
    <NavigationDialog />
    </Box>
  );
};

export default SingleArticle;